import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { LocaleService } from '@next/core-lib/i18n';
import { map, Observable } from 'rxjs';
import { Environment } from 'src/environments/environment.model';

import { signalStoreCustomerRequestOptions } from '../../shared/interceptors/signal-store-customer-context';
import {
  BasicSpray,
  CreateSprayApplicationData,
  EligibleCropFieldsForSprayApplicationsResponse,
  SprayApplication,
  SprayApplicationResponse,
} from '../../shared/models';
import {
  BlightCropFieldDataWrapperAPIResponse,
  BlightCropFieldsDataWrapperAPIResponse,
  CustomerBlightRightsAPIResponse,
  GetCropFieldRegistrationResponse,
  GetParticipatingCropFieldsResponse,
} from '../models/api-models';
import {
  mapBlightChartDetailAPIResponses,
  mapBlightDataAPIResponses,
  mapCustomerBlightRightsAPIResponse,
  mapParticipatingCropFieldAPIResponses,
} from '../models/api-mappers';

import {
  BlightChartDetail,
  BlightData,
  CropFieldRegistration,
  CustomerBlightRights,
  ParticipatingCropField,
  PhytophthoraPressure,
} from '../models/models';
import { phytophthoraPressureApiMap } from '../shared/helpers/blight-helpers';

@Injectable({
  providedIn: 'root',
})
export class BlightService {
  protected http = inject(HttpClient);
  private readonly env = inject<Environment>(EnvironmentInjector);
  private readonly locale = inject(LocaleService);

  protected baseUrl = this.env.APIM_BASE_URL + `/arable-disease-integration-service/`;
  protected cropFieldsEndpoint = 'crop-fields';
  protected sprayEndpoint = 'sprays/';
  protected sprayApplicationEndpoint = 'spray-applications/';
  protected arableAdviceEndpoint = this.env.APIM_BASE_URL + '/arable-advice-service';

  getBlightData(from: Date, until: Date, customerId: number | undefined, cropYear: number): Observable<BlightData[]> {
    const toDateString = (date: Date) => {
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const fromDateString = toDateString(from);
    const untilDateString = toDateString(until);

    const url = `${this.baseUrl}${this.cropFieldsEndpoint}?cropYear=${cropYear}&fromDate=${fromDateString}&untilDate=${untilDateString}`;
    return this.http.get<BlightCropFieldsDataWrapperAPIResponse>(url, signalStoreCustomerRequestOptions(customerId))
      .pipe(map((x) => mapBlightDataAPIResponses(x.cropFields)));
  }

  getCustomerBlightRights(customerId?: number, cropYear?: number): Observable<CustomerBlightRights> {
    const url = `${this.baseUrl}customer/is-premium`;
    return this.http.get<CustomerBlightRightsAPIResponse>(url, {
      params: cropYear == null ? undefined : { cropYear },
      ...signalStoreCustomerRequestOptions(customerId),
    })
      .pipe(map(mapCustomerBlightRightsAPIResponse));
  }

  getBlightChartDetailsByCropField(
    cropYear: number,
    cropFieldId: number,
    from: string,
    until: string,
    customerId?: number,
  ): Observable<BlightChartDetail[]> {
    const url = `${this.baseUrl}${this.cropFieldsEndpoint}/${cropFieldId}/${cropYear}?fromDate=${from}&untilDate=${until}`;
    return this.http
      .get<BlightCropFieldDataWrapperAPIResponse>(url, signalStoreCustomerRequestOptions(customerId))
      .pipe(map(mapBlightChartDetailAPIResponses));
  }

  getActiveSprays(onDate: Date) {
    const url = `${this.baseUrl}${this.sprayEndpoint}active`;
    return this.http.get<BasicSpray[]>(url, { params: { onDate: onDate.toISOString() } });
  }

  createSprayApplication(createSprayApplicationData: CreateSprayApplicationData) {
    const url = `${this.baseUrl}${this.sprayApplicationEndpoint}${createSprayApplicationData.cropFieldId}`;
    return this.http.patch<SprayApplication>(url, createSprayApplicationData);
  }

  deleteSprayApplication(sprayApplicationId: number, cropFieldId: number, cropYear: number) {
    const url = `${this.baseUrl}${this.sprayApplicationEndpoint}${cropFieldId}/${cropYear}/${sprayApplicationId}`;
    return this.http.delete(url);
  }

  getSprayApplications(cropFieldIds: number[], cropYear: number): Observable<SprayApplication[]> {
    const url = `${this.baseUrl}${this.sprayApplicationEndpoint}`;
    return this.http.get<SprayApplicationResponse>(url, {
      params: {
        cropFieldIds: cropFieldIds.map((id) => id.toString()),
        cropYear: cropYear.toString(),
      },
    }).pipe(map((response) => response.sprayApplications));
  }

  getCropFieldsForSprayApplications(date: string) {
    const url = `${this.baseUrl}${this.sprayApplicationEndpoint}cropfields`;
    return this.http.get<EligibleCropFieldsForSprayApplicationsResponse>(url, { params: { forSprayDate: date } })
      .pipe(map((response) => response.cropFieldIds));
  }

  getParticipatingCropFields(cropYear: number, customerId?: number): Observable<ParticipatingCropField[]> {
    const url = `${this.baseUrl}${this.cropFieldsEndpoint}/${cropYear}/participating`;
    return this.http
      .get<GetParticipatingCropFieldsResponse>(url, signalStoreCustomerRequestOptions(customerId))
      .pipe(map(mapParticipatingCropFieldAPIResponses));
  }

  getCropFieldRegistration(cropFieldId: number, cropYear: number, customerId?: number): Observable<CropFieldRegistration> {
    const url = `${this.baseUrl}${this.cropFieldsEndpoint}/${cropFieldId}/${cropYear}?fromDate=${cropYear}-1-1&untilDate=${cropYear}-1-1`;
    return this.http
      .get<GetCropFieldRegistrationResponse>(url, signalStoreCustomerRequestOptions(customerId))
      .pipe(map((response) => response.cropField));
  }

  updatePlantDate(cropFieldId: number, newPlantDate: Date, customerId?: number) {
    const url = `${this.arableAdviceEndpoint}/crop-fields/${cropFieldId}/plant-date`;
    return this.http.patch<CropFieldRegistration>(
      url,
      { customerPlantDate: newPlantDate },
      signalStoreCustomerRequestOptions(customerId),
    );
  }

  updateEmergenceDate(cropFieldId: number, newEmergenceDate: Date, customerId?: number) {
    const url = `${this.arableAdviceEndpoint}/crop-fields/${cropFieldId}/emergence-date`;
    return this.http.patch<CropFieldRegistration>(
      url,
      { customerEmergenceDate: newEmergenceDate },
      signalStoreCustomerRequestOptions(customerId),
    );
  }

  updateHarvestDate(cropFieldId: number, newHarvestDate: Date, customerId?: number) {
    const url = `${this.arableAdviceEndpoint}/crop-fields/${cropFieldId}/harvest-date`;
    return this.http.patch<CropFieldRegistration>(
      url,
      { customerHarvestDate: newHarvestDate },
      signalStoreCustomerRequestOptions(customerId),
    );
  }

  updatePhytophthoraPressure(
    cropFieldId: number,
    year: number,
    phytophthoraPressure: PhytophthoraPressure,
    customerId?: number,
  ) {
    const url = `${this.baseUrl}${this.cropFieldsEndpoint}/${cropFieldId}/${year}/disease-pressure`;
    return this.http.patch<CropFieldRegistration>(
      url,
      { diseasePressure: phytophthoraPressureApiMap[phytophthoraPressure] },
      signalStoreCustomerRequestOptions(customerId),
    );
  }

  updateSuppressSprayAdvice(cropFieldId: number, year: number, suppressSprayAdvice: boolean, customerId?: number) {
    const url = `${this.baseUrl}${this.cropFieldsEndpoint}/${cropFieldId}/${year}/suppress-spray-advice`;
    return this.http.patch<CropFieldRegistration>(
      url,
      { suppressSprayAdvice: suppressSprayAdvice },
      signalStoreCustomerRequestOptions(customerId),
    );
  }

  updateCropFieldVisibilityStatus(cropFieldId: number, year: number, isVisible: boolean, customerId?: number) {
    const url = `${this.baseUrl}${this.cropFieldsEndpoint}/${cropFieldId}/${year}/visibility`;
    return this.http.patch<CropFieldRegistration>(
      url,
      { isVisible: isVisible },
      signalStoreCustomerRequestOptions(customerId),
    );
  }

  recalculateBlightData(cropFieldId: number, _: number, customerId?: number) {
    const url = `${this.baseUrl}${this.cropFieldsEndpoint}/${cropFieldId}/recalculate`;
    return this.http.post(url, {}, signalStoreCustomerRequestOptions(customerId));
  }

  /** @deprecated - will be replaced with a helper function in the new BlightV2 folder */
  getSprayTypeTranslationForMapLayer(value: number | string): string {
    const sprayTypeTranslations: { [value: number | string]: string } = {
      1: 'blight.mapLayerSprayType.preventiveSpray',
      2: 'blight.mapLayerSprayType.curativeSpray',
      3: 'blight.mapLayerSprayType.stopSpray',
    };

    return this.locale.translate(sprayTypeTranslations[value.toString()] || 'defaultTranslationKey');
  }
}
